import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { buildTicketPreview } from '../libs/printer/TsplTicketBuilder';

const DOTS_PER_MM = 8;
const PAPER_WIDTH_MM = 80;
const PRINTABLE_MARGIN_DOTS = 32;
// Courier New ocupa aproximadamente 0.6 em por caracter. Estos tamaños reproducen
// los anchos TSPL de 8, 12 y 16 puntos usados para calcular el centrado.
const FONT_SIZE = { 1: 13.33, 2: 20, 3: 26.67 };

const parseTicket = (tspl) => {
    const lines = tspl.split(/\r?\n/);
    const sizeLine = lines.find((line) => line.startsWith('SIZE ')) || '';
    const sizeMatch = sizeLine.match(/SIZE\s+[\d.]+\s*mm,\s*([\d.]+)\s*mm/i);
    const heightMm = sizeMatch ? Number(sizeMatch[1]) : 200;
    const elements = [];

    lines.forEach((line, index) => {
        const textMatch = line.match(
            /^TEXT\s+(-?\d+),(\d+),"([^"]+)",0,(\d+),(\d+)(?:,(\d+))?,"(.*)"$/
        );
        if (textMatch) {
            elements.push({
                type: 'text',
                key: `text-${index}`,
                x: Number(textMatch[1]),
                y: Number(textMatch[2]),
                font: textMatch[3],
                xScale: Number(textMatch[4]),
                yScale: Number(textMatch[5]),
                alignment: Number(textMatch[6] || 1),
                value: textMatch[7]
            });
            return;
        }

        const barMatch = line.match(/^BAR\s+(-?\d+),(\d+),(\d+),(\d+)$/);
        if (barMatch) {
            elements.push({
                type: 'bar',
                key: `bar-${index}`,
                x: Number(barMatch[1]),
                y: Number(barMatch[2]),
                width: Number(barMatch[3]),
                height: Number(barMatch[4])
            });
            return;
        }

        const qrMatch = line.match(/^QRCODE\s+(-?\d+),(\d+),[^,]+,(\d+),[^"]+"(.*)"$/);
        if (qrMatch) {
            elements.push({
                type: 'qr',
                key: `qr-${index}`,
                x: Number(qrMatch[1]),
                y: Number(qrMatch[2]),
                cellSize: Number(qrMatch[3]),
                value: qrMatch[4]
            });
        }
    });

    return { heightMm, elements };
};

const TicketTsplPreview = ({ configuration }) => {
    const ticket = parseTicket(buildTicketPreview(configuration));
    const paperWidth = PAPER_WIDTH_MM * DOTS_PER_MM;
    const paperHeight = ticket.heightMm * DOTS_PER_MM;

    return (
        <div
            className="border rounded p-3"
            style={{ background: '#d8d8d8', maxHeight: '62vh', overflow: 'auto' }}
        >
            <div
                className="mx-auto position-relative"
                style={{
                    width: paperWidth,
                    height: paperHeight,
                    background: '#fff',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.25)',
                    overflow: 'hidden'
                }}
            >
                {ticket.elements.map((element) => {
                    const left = PRINTABLE_MARGIN_DOTS + element.x;
                    if (element.type === 'text') {
                        const fontSize = (FONT_SIZE[element.font] || FONT_SIZE[2]) * element.yScale;
                        return (
                            <span
                                key={element.key}
                                style={{
                                    position: 'absolute',
                                    left,
                                    top: element.y,
                                    color: '#000',
                                    fontFamily: '"Courier New", monospace',
                                    fontSize,
                                    fontWeight: element.font === '3' ? 700 : 500,
                                    lineHeight: 1,
                                    letterSpacing: 0,
                                    whiteSpace: 'nowrap',
                                    transform: `${
                                        element.alignment === 2
                                            ? 'translateX(-50%) '
                                            : element.alignment === 3
                                                ? 'translateX(-100%) '
                                                : ''
                                    }scaleX(${element.xScale})`,
                                    transformOrigin: element.alignment === 2 ? 'center top' : 'left top'
                                }}
                            >
                                {element.value}
                            </span>
                        );
                    }

                    if (element.type === 'bar') {
                        return (
                            <span
                                key={element.key}
                                style={{
                                    position: 'absolute',
                                    left,
                                    top: element.y,
                                    width: element.width,
                                    height: Math.max(1, element.height),
                                    background: '#000'
                                }}
                            />
                        );
                    }

                    const qrSize = Math.max(180, element.cellSize * 32.67);
                    return (
                        <QRCodeSVG
                            key={element.key}
                            value={element.value}
                            size={qrSize}
                            level="L"
                            includeMargin={false}
                            style={{
                                position: 'absolute',
                                left,
                                top: element.y
                            }}
                        />
                    );
                })}
            </div>
        </div>
    );
};

export default TicketTsplPreview;
